import React from 'react';
import { useCompany } from './useCompany';
import { useAuth } from '../../context/AuthContext';
import '../../styles/document-template.css';
import { makeMoneyFormatter, getDisplayCurrency, convertAmount } from '../../utils/currency';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d + (d.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const STATUS_MAP = {
  paid: { cls: 'doc-status-paid', label: 'Paid' },
  pending: { cls: 'doc-status-pending', label: 'Pending' },
  overdue: { cls: 'doc-status-overdue', label: 'Overdue' },
  draft: { cls: 'doc-status-draft', label: 'Draft' },
  sent: { cls: 'doc-status-sent', label: 'Sent' },
  approved: { cls: 'doc-status-approved', label: 'Approved' },
  accepted: { cls: 'doc-status-accepted', label: 'Accepted' },
  rejected: { cls: 'doc-status-rejected', label: 'Rejected' },
  cancelled: { cls: 'doc-status-cancelled', label: 'Cancelled' },
  voided: { cls: 'doc-status-voided', label: 'Voided' },
  void: { cls: 'doc-status-voided', label: 'Void' },
  confirmed: { cls: 'doc-status-confirmed', label: 'Confirmed' },
  completed: { cls: 'doc-status-completed', label: 'Completed' },
  processing: { cls: 'doc-status-processing', label: 'Processing' },
  viewed: { cls: 'doc-status-viewed', label: 'Viewed' },
  partial: { cls: 'doc-status-partial', label: 'Partial' },
  expired: { cls: 'doc-status-expired', label: 'Expired' },
  closed: { cls: 'doc-status-draft', label: 'Closed' },
  received: { cls: 'doc-status-completed', label: 'Received' },
  partiallyreceived: { cls: 'doc-status-partial', label: 'Partial' },
  partiallyfulfilled: { cls: 'doc-status-partial', label: 'Partial' },
  partiallypaid: { cls: 'doc-status-partial', label: 'Partial' },
  shipped: { cls: 'doc-status-sent', label: 'Shipped' },
};

function getStatus(status) {
  if (!status) return null;
  const s = status.toLowerCase().replace(/[\s_-]/g, '');
  for (const [key, val] of Object.entries(STATUS_MAP)) {
    if (s === key.replace(/[\s_-]/g, '')) return val;
  }
  return { cls: 'doc-status-draft', label: status };
}

/**
 * Unified premium document template.
 * Props:
 *   docType: 'invoice' | 'estimate' | 'sales-order' | 'purchase-order' | 'bill'
 *   data: document data object
 *   onAction: callback for CTA button clicks
 */
export default function DocumentTemplate({ docType = 'invoice', data = {}, onAction }) {
  const company = useCompany();
  const items = data.items || [];
  const currency = data.currency || getDisplayCurrency();
  const baseCurrency = company?.base_currency || getDisplayCurrency();
  const fmt = makeMoneyFormatter(currency);
  const fmtBase = makeMoneyFormatter(baseCurrency);

  const TITLE_MAP = {
    'invoice': 'INVOICE',
    'estimate': 'ESTIMATE',
    'sales-order': 'SALES ORDER',
    'purchase-order': 'PURCHASE ORDER',
    'bill': 'BILL',
  };

  const NUMBER_MAP = {
    'invoice': data.invoice_number,
    'estimate': data.estimate_number,
    'sales-order': data.order_number,
    'purchase-order': data.po_number,
    'bill': data.bill_number,
  };

  const DATE_MAP = {
    'invoice': data.invoice_date,
    'estimate': data.estimate_date,
    'sales-order': data.order_date,
    'purchase-order': data.order_date,
    'bill': data.bill_date,
  };

  const DUE_MAP = {
    'invoice': data.due_date,
    'estimate': data.expiration_date,
    'bill': data.due_date,
  };

  const TITLE_LABEL = TITLE_MAP[docType] || 'DOCUMENT';
  const docNumber = NUMBER_MAP[docType] || data.document_number || '—';
  const docDate = DATE_MAP[docType] || data.date || '—';
  const dueDate = DUE_MAP[docType];
  const status = getStatus(data.status);
  const isCustomerType = ['invoice', 'estimate', 'sales-order'].includes(docType);
  const NUMBER_LABEL = {
    'invoice': 'Invoice #',
    'estimate': 'Estimate #',
    'sales-order': 'Sales Order #',
    'purchase-order': 'PO #',
    'bill': 'Bill #',
  }[docType] || 'Document #';
  const balanceDue = parseFloat(data.amount_due ?? (data.total - (data.amount_paid || 0))) || 0;
  const amountPaid = parseFloat(data.amount_paid) || 0;
  const total = parseFloat(data.total) || 0;
  const hasSKU = items.some(i => i.sku);

  // Column count based on whether SKU exists
  const colCount = hasSKU ? 7 : 6;

  // Meta rows for the right-side table
  const metaRows = [
    [NUMBER_LABEL, docNumber],
    ['Date', fmtDate(docDate)],
    dueDate && [docType === 'estimate' ? 'Expires' : 'Due Date', fmtDate(dueDate)],
    data.payment_terms && ['Terms', data.payment_terms],
    data.currency && ['Currency', data.currency],
    status && ['Status', <span key="st" className={`doc-info-badge ${status.cls}`}>{status.label}</span>],
  ].filter(Boolean);

  return (
    <div className="doc">
      {/* ---- LETTERHEAD (print only) ---- */}
      <div className="doc-letterhead">
        {company?.logo_path ? (
          <img src={company.logo_path} alt="Logo" className="doc-letterhead-logo" />
        ) : (
          <div style={{ width: 50, height: 50, borderRadius: 8, background: '#0f172a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800 }}>LP</div>
        )}
        <div>
          <div className="doc-letterhead-name">{company?.name || 'LedgerPro'}</div>
          {company?.address_line1 && <div className="doc-letterhead-line">{company.address_line1}</div>}
          {(company?.city || company?.state) && <div className="doc-letterhead-line">{[company.city, company.state].filter(Boolean).join(', ')}</div>}
          {company?.phone && <div className="doc-letterhead-line">Phone: {company.phone}</div>}
          {company?.email && <div className="doc-letterhead-line">{company.email}</div>}
        </div>
      </div>

      {/* ---- HEADER ---- */}
      <div className="doc-header">
        <div className="doc-header-left">
          <div className="doc-header-logo-row">
            {company?.logo_path ? (
              <img
                src={company.logo_path}
                alt="Company Logo"
                className="doc-header-logo"
                style={{ width: 200, height: 150, objectFit: 'contain', background: '#fff' }}
              />
            ) : (
              <div style={{ width: 150, height: 150, borderRadius: 12, background: '#0f172a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, fontWeight: 900, letterSpacing: '-0.02em', border: '3px solid #1e3a5f' }}>
                LP
              </div>
            )}
            <div className="doc-header-text">
              <div className="doc-company-name">{company?.name || 'LedgerPro'}</div>
              {company?.address_line1 && <div className="doc-company-line">{company.address_line1}</div>}
              {(company?.city || company?.state || company?.postal_code) && (
                <div className="doc-company-line">{[company.city, company.state, company.postal_code].filter(Boolean).join(', ')}</div>
              )}
              {company?.country && <div className="doc-company-line">{company.country}</div>}
              {company?.phone && <div className="doc-company-line">Phone: {company.phone}</div>}
              {company?.email && <div className="doc-company-line">{company.email}</div>}
              {company?.website && <div className="doc-company-line">{company.website}</div>}
            </div>
          </div>
        </div>
        <div className="doc-header-right">
          {status && <div className={`doc-title-badge ${status.cls}`}>{status.label}</div>}
          <div className="doc-title">{TITLE_LABEL}</div>
        </div>
      </div>

      {/* ---- META BAND: party (left) + document info table (right), QBO style ---- */}
      <div className="doc-meta-band">
        <div className="doc-parties">
          <div className="doc-party">
            <div className="doc-party-label">
              {isCustomerType ? 'Bill To' : 'Vendor / Supplier'}
            </div>
            {isCustomerType ? (
              <>
                <div className="doc-party-name">{data.customer_name || data.customer_company_name || '—'}</div>
                {data.customer_company_name && data.customer_name !== data.customer_company_name && (
                  <div className="doc-party-line">{data.customer_company_name}</div>
                )}
                {data.customer_address && <div className="doc-party-line">{data.customer_address}</div>}
                {data.customer_email && <div className="doc-party-line">{data.customer_email}</div>}
                {data.customer_phone && <div className="doc-party-line">{data.customer_phone}</div>}
                {data.customer_tax_id && <div className="doc-party-tax">Tax/VAT: {data.customer_tax_id}</div>}
              </>
            ) : (
              <>
                <div className="doc-party-name">{data.vendor_name || data.vendor_company_name || '—'}</div>
                {data.vendor_company_name && data.vendor_name !== data.vendor_company_name && (
                  <div className="doc-party-line">{data.vendor_company_name}</div>
                )}
                {data.vendor_address && <div className="doc-party-line">{data.vendor_address}</div>}
                {data.vendor_email && <div className="doc-party-line">{data.vendor_email}</div>}
                {data.vendor_phone && <div className="doc-party-line">{data.vendor_phone}</div>}
                {data.vendor_tax_id && <div className="doc-party-tax">Tax/VAT: {data.vendor_tax_id}</div>}
              </>
            )}
          </div>

          {(docType === 'purchase-order' || docType === 'sales-order') && data.shipping_address && (
            <div className="doc-party">
              <div className="doc-party-label">Ship To</div>
              <div className="doc-party-line">{data.shipping_address}</div>
            </div>
          )}
        </div>

        <table className="doc-meta-table">
          <tbody>
            {metaRows.map(([label, value], i) => (
              <tr key={i}>
                <td className="doc-meta-label">{label}</td>
                <td className="doc-meta-value">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---- LINE ITEMS TABLE ---- */}
      <div className="doc-table-wrap">
        <table className="doc-table">
          <thead>
            <tr>
              <th style={{ width: hasSKU ? '38%' : '48%' }}>Description</th>
              {hasSKU && <th style={{ width: '10%' }}>SKU</th>}
              <th className="text-right" style={{ width: '8%' }}>Qty</th>
              <th className="text-right" style={{ width: '14%' }}>Rate</th>
              {(docType === 'invoice' || docType === 'estimate' || docType === 'sales-order') && <th className="text-right" style={{ width: '10%' }}>Discount</th>}
              <th className="text-right" style={{ width: '10%' }}>Tax</th>
              <th className="text-right" style={{ width: '14%' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={colCount} className="doc-empty">No line items</td></tr>
            )}
            {items.map((item, i) => (
              <tr key={i}>
                <td className="doc-item-cell">
                  {(() => {
                  const name = item.product_name || item.item_name;
                  const lineDesc = item.description && item.description !== name ? item.description : null;
                  const showDesc = lineDesc || item.product_description;
                  return (
                    <>
                      {name && <div className="doc-item-name">{name}</div>}
                      {showDesc
                        ? <div className="doc-item-desc">{showDesc}</div>
                        : !name ? <div className="doc-item-desc">—</div>
                        : null}
                    </>
                  );
                })()}
                </td>
                {hasSKU && <td className="doc-sku">{item.sku || '—'}</td>}
                <td className="text-right">{item.quantity}</td>
                <td className="text-right">{fmt(item.unit_price)}</td>
                {(docType === 'invoice' || docType === 'estimate' || docType === 'sales-order') && (
                  <td className="text-right">{item.discount_percent ? `${item.discount_percent}%` : (item.discount_amount ? fmt(item.discount_amount) : '—')}</td>
                )}
                <td className="text-right">{fmt(item.tax_amount)}</td>
                <td className="text-right doc-amount">{fmt(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---- FINANCIAL SUMMARY ---- */}
      <div className="doc-totals-wrap">
        <div className="doc-totals">
          <div className="doc-total-row"><span>Subtotal</span><span>{fmt(data.subtotal)}</span></div>
          {parseFloat(data.discount_amount) > 0 && (
            <div className="doc-total-row"><span>Discount</span><span style={{ color: '#dc2626' }}>-{fmt(data.discount_amount)}</span></div>
          )}
          {parseFloat(data.tax_amount) > 0 && (
            <div className="doc-total-row"><span>Tax</span><span>{fmt(data.tax_amount)}</span></div>
          )}
          {parseFloat(data.shipping_amount) > 0 && (
            <div className="doc-total-row"><span>Shipping</span><span>{fmt(data.shipping_amount)}</span></div>
          )}
          <div className="doc-total-row doc-total-grand"><span>Grand Total</span><span>{fmt(total)}</span></div>
          {currency !== baseCurrency && total > 0 && (
            <div className="doc-total-row doc-fx-total"><span>≈ {baseCurrency} Equivalent</span><span>{fmtBase(convertAmount(total, data.exchange_rate))}</span></div>
          )}
          {amountPaid > 0 && (
            <div className="doc-total-row"><span>Paid</span><span style={{ color: '#16a34a' }}>−{fmt(amountPaid)}</span></div>
          )}
          {balanceDue > 0 && (
            <div className={`doc-total-row doc-total-balance ${balanceDue > 0 && status?.cls === 'doc-status-overdue' ? 'overdue' : balanceDue === 0 ? 'paid' : 'pending'}`}>
              <span>Balance Due</span><span>{fmt(balanceDue)}</span>
            </div>
          )}
          {balanceDue === 0 && amountPaid > 0 && (
            <div className="doc-total-row doc-total-balance paid">
              <span>Fully Paid ✓</span>
            </div>
          )}
        </div>
      </div>

      {/* ---- PAYMENT PROGRESS (invoices & bills) ---- */}
      {(docType === 'invoice' || docType === 'bill') && amountPaid > 0 && total > 0 && (() => {
        const pct = Math.min(100, Math.round((amountPaid / total) * 100));
        const isFull = pct >= 100;
        return (
          <div className="doc-payment-progress" style={{ background: isFull ? '#f0fdf4' : '#f8fafc', borderColor: isFull ? '#bbf7d0' : '#e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3 }}>Payment Progress</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: isFull ? '#16a34a' : pct >= 50 ? '#2563eb' : '#d97706' }}>{pct}%</span>
            </div>
            <div className="doc-payment-progress-bar">
              <div className="doc-payment-progress-fill" style={{ width: `${pct}%`, background: isFull ? '#16a34a' : pct >= 50 ? '#2563eb' : '#d97706' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8' }}>
              <span>Paid: {fmt(amountPaid)}</span>
              <span>{isFull ? 'Fully paid ✓' : `Remaining: ${fmt(total - amountPaid)}`}</span>
            </div>
          </div>
        );
      })()}

      {/* ---- NOTES ---- */}
      {(data.memo || data.notes) && (
        <div className="doc-notes">
          <div className="doc-notes-title">Notes</div>
          <div className="doc-notes-text">{data.memo || data.notes}</div>
        </div>
      )}

      {/* ---- TERMS & CONDITIONS ---- */}
      {data.terms && (
        <div className="doc-notes doc-terms">
          <div className="doc-notes-title">Terms &amp; Conditions</div>
          <div className="doc-notes-text">{data.terms}</div>
        </div>
      )}

      {/* ---- SIGNATURES (estimate, sales order, purchase order) ---- */}
      {['estimate', 'sales-order', 'purchase-order'].includes(docType) && (
        <div className="doc-signatures">
          <div className="doc-signature">
            <div className="doc-signature-line" />
            <div className="doc-signature-label">Prepared By</div>
          </div>
          <div className="doc-signature">
            <div className="doc-signature-line" />
            <div className="doc-signature-label">{isCustomerType ? 'Customer Signature' : 'Vendor Signature'}</div>
          </div>
          <div className="doc-signature">
            <div className="doc-signature-line" />
            <div className="doc-signature-label">Date</div>
          </div>
        </div>
      )}

      {/* ---- CTA AREA (estimate) ---- */}
      {docType === 'estimate' && data.status === 'Sent' && onAction && (
        <div className="doc-cta">
          <button className="doc-cta-btn" onClick={() => onAction('accept')}>
            Accept Estimate
          </button>
        </div>
      )}

      {/* ---- FOOTER ---- */}
      <div className="doc-footer">
        <div className="doc-footer-thanks">Thank you for your business!</div>
        <div className="doc-footer-info">
          {company?.name && <strong>{company.name}</strong>}
          {company?.phone && <span> · Phone: {company.phone}</span>}
          {company?.email && <span> · {company.email}</span>}
          {company?.website && <span> · {company.website}</span>}
          {company?.address_line1 && <div>{company.address_line1}{company.city ? `, ${company.city}` : ''}{company.state ? `, ${company.state}` : ''}</div>}
        </div>
      </div>
    </div>
  );
}
